import {
  ArrowUpRight,
  Folder,
  CircleCheck,
  Hourglass,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import {
  ConflictsIcon,
  NoIcon,
  PartialIcon,
} from "@/features/dashboard/pages/specification/components/reports-related-reusables/item-results-section/icons";
import { Link } from "react-router-dom";
import RowActions from "./row-actions/row-actions";
import { useState } from "react";
import { ISupplierResponsesAllItem } from "@/shared/services/interface/responses/ISupplierResponsesAllResponse";
import AvatarGroup from "./components/avatar-group";
import OutlineImageWithFallback from "@/shared/components/outline-image-with-fallback/outline-image-with-fallback";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { useSelector } from "@/core/redux/store";
import { RootState } from "@/core/redux/store";
import HighlightText from "../../utils/HighlightText";
import useGridTemplate from "../../utils/useGridTemplate";
import TableRowDetails from "./table-row-details/table-row-details";
import { getDashboardLink } from "@/shared/utils/link";
import { ROUTES } from "@/core/router/routes";

interface TableRowProps {
  data: ISupplierResponsesAllItem;
}

export default function TableRow({ data }: TableRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { search, hiddenColumnFields } = useSelector(
    (state: RootState) => state.supplierResponsesAll
  );
  const companyId = useSelector(
    (state: RootState) => state.user.me?.company?.id
  );

  const isColumnVisible = (field: string) => {
    return !hiddenColumnFields.includes(field);
  };

  const handleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Map suppliers to user format for UserCircle component
  const respondedUsers = (data.respondedSuppliers?.suppliers || [])
    .filter((supplier) => supplier)
    .map((supplier) => ({
      name:
        supplier.supplierName ||
        supplier.supplierEmail ||
        supplier.supplierId ||
        "Tedarikçi",
      email:
        supplier.supplierEmail || supplier.supplierId || "email@example.com",
      avatar: null,
    }));

  const notRespondedUsers = (data.notRespondedSuppliers?.suppliers || [])
    .filter((supplier) => supplier)
    .map((supplier) => ({
      name:
        supplier.supplierName ||
        supplier.supplierEmail ||
        supplier.supplierId ||
        "Tedarikçi",
      email:
        supplier.supplierEmail || supplier.supplierId || "email@example.com",
      avatar: null,
    }));

  // Determine status based on item counts
  const getStatus = (): "ongoing" | "completed" | "waiting" => {
    const respondedCount = data.respondedSuppliers?.totalCount || 0;
    const notRespondedCount = data.notRespondedSuppliers?.totalCount || 0;

    if (respondedCount === 0) {
      return "waiting";
    }
    if (respondedCount > 0 && notRespondedCount > 0) {
      return "ongoing";
    }
    if (notRespondedCount === 0) {
      return "completed";
    }
    return "ongoing";
  };

  return (
    <>
      <div
        className="grid items-center w-full h-[56px] last:border-b-0 border-b border-border"
        style={{
          gridTemplateColumns: useGridTemplate(),
        }}
      >
        {/* Category Title - flex-1 */}
        <div
          className="flex items-center cursor-pointer pl-[45px] pr-3 py-2 relative  h-full"
          onClick={handleExpand}
        >
          <div className="flex items-center justify-center w-[45px] h-full absolute left-0 top-0">
            <div className="flex items-center justify-center w-8 h-8">
              <ChevronDown
                className={`w-4 h-4 text-foreground transition-transform duration-200 ${
                  isExpanded ? "rotate-0" : "-rotate-90"
                }`}
              />
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <p className="text-xs font-600 text-foreground leading-4 w-[180px] min-[1250px]:w-[170px] min-[1350px]:w-[200px] min-[1420px]:w-[200px]  min-[1490px]:w-[250px] truncate">
              <Link
                to={`${getDashboardLink(
                  ROUTES.Dashboard.Specification.Create
                )}?specificationId=${data.specificationId}`}
                onClick={(e) => e.stopPropagation()}
                className="hover:underline"
              >
                <HighlightText text={data.specificationName} search={search} />
              </Link>
            </p>
            {data.folderInfo && (
              <div className="flex items-center gap-1.5">
                <Folder className="w-3 h-3 text-muted-foreground" />
                {data.folderInfo.depth > 1 ? (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center gap-1.5">
                          <p className="text-[11px] text-muted-foreground leading-4">
                            <HighlightText
                              text={data.folderInfo.folderName}
                              search={search}
                            />
                          </p>
                          <span className="text-[11px] text-muted-foreground">
                            ·
                          </span>
                          <p className="text-[11px] text-muted-foreground leading-4">
                            +{data.folderInfo.depth - 1}
                          </p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">
                          {data.folderInfo.parentFolderName || "Parent Folder"}{" "}
                          / {data.folderInfo.folderName}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  <p className="text-[11px] text-muted-foreground leading-4">
                    <HighlightText
                      text={data.folderInfo.folderName}
                      search={search}
                    />
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Avatar Group - w-[136px] */}
        <div className="flex items-center gap-1 px-[8px] h-full relative  ">
          <TooltipProvider>
            <div className="flex items-center gap-1">
              {(data.productsInfo?.products || [])
                .slice(0, 2)
                .map((product) => (
                  <Tooltip key={product.productId}>
                    <TooltipTrigger asChild>
                      <div>
                        <OutlineImageWithFallback
                          src={product.logoSrc || ""}
                          alt={product.productName}
                          name={product.productName}
                          containerSize={32}
                          imageSize={22}
                          className=""
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="text-xs">
                        <HighlightText
                          text={product.productName}
                          search={search}
                        />
                      </p>
                    </TooltipContent>
                  </Tooltip>
                ))}
              {(data.productsInfo?.totalCount || 0) > 2 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-8 h-8 rounded-md border border-border bg-muted flex items-center justify-center cursor-pointer hover:bg-muted/80 transition-colors">
                      <span className="text-xs text-muted-foreground">
                        +{(data.productsInfo?.totalCount || 0) - 2}
                      </span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">
                      {(data.productsInfo?.totalCount || 0) - 2} more
                    </p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </TooltipProvider>
        </div>

        {/* Number - w-[111px] - itemCount */}
        {isColumnVisible("itemCount") && (
          <div className="flex items-center justify-center px-[8px] h-full relative ">
            <p className="text-sm font-600 text-foreground leading-5">
              {data.totalItemCount || 0}
            </p>
          </div>
        )}

        {/* Nested Avatar - w-[117px] - answered */}
        {isColumnVisible("answered") && (
          <div className="flex items-center justify-center px-[8px] h-full relative ">
            <AvatarGroup users={respondedUsers} maxDisplay={3} size="md" />
          </div>
        )}

        {/* Avatar - w-[111px] - notAnswered */}
        {isColumnVisible("notAnswered") && (
          <div className="flex items-center justify-center px-[8px] h-full relative ">
            <AvatarGroup users={notRespondedUsers} maxDisplay={2} size="md" />
          </div>
        )}

        {/* Toggle Group - w-[193px] - itemStatus */}
        {isColumnVisible("itemStatus") && (
          <div className="flex items-center justify-center px-[8px] h-full relative ">
            <div className="bg-background border border-border rounded-full flex items-center gap-0 px-2">
              <div className="flex items-center gap-1 px-1.5 h-[28px] border-r border-border">
                <CircleCheck color="#50AC5A" height={12} width={12} />
                <span className="text-xs text-foreground leading-4">
                  {data.itemStatus?.answeredYes || 0}
                </span>
              </div>
              <div className="flex items-center gap-1 px-1.5 h-[28px] border-r border-border">
                <PartialIcon className="!size-[12px]" />
                <span className="text-xs text-foreground leading-4">
                  {data.itemStatus?.answeredPartially || 0}
                </span>
              </div>
              <div className="flex items-center gap-1 px-1.5 h-[28px] border-r border-border">
                <NoIcon className="!size-[12px]" />
                <span className="text-xs text-foreground leading-4">
                  {data.itemStatus?.answeredNo || 0}
                </span>
              </div>
              <div className="flex items-center gap-1 px-1.5 h-[28px]">
                <ConflictsIcon className="!size-[12px]" />
                <span className="text-xs text-foreground leading-4">
                  {data.itemStatus?.notFound || 0}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Status Badge - w-[139px] - status */}
        {isColumnVisible("status") && (
          <div className="flex items-center justify-center px-[8px] h-full relative ">
            <SupplierStatusBadge status={getStatus()} />
          </div>
        )}

        <div className="flex">
          {/* Action 1 - w-[41px] */}
          <div className="flex items-center justify-center  h-full relative w-[41px]">
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={`/dashboard/specification/supplier-responses?specificationId=${data.specificationId}`}
                  target="_blank"
                  className="flex items-center justify-center w-8 h-8 px-[8px] bg-white rounded cursor-pointer hover:bg-accent transition-colors"
                >
                  <ArrowUpRight className="w-4 h-4 text-foreground" />
                </Link>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Detail</p>
              </TooltipContent>
            </Tooltip>
          </div>

          {/* Action 2 - w-[41px] */}
          <div className="flex items-center justify-center px-[8px] h-full relative w-[41px]">
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <RowActions
                    specificationId={data.specificationId}
                    companyId={companyId || ""}
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="text-xs">Actions</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      </div>
      {isExpanded && <TableRowDetails specificationId={data.specificationId} />}
    </>
  );
}

const SupplierStatusBadge = ({
  status,
}: {
  status: "ongoing" | "completed" | "waiting";
}) => {
  if (status === "completed") {
    return (
      <div className="bg-[#F4F4F5] text-foreground rounded-full px-[6px] py-1 flex items-center gap-[4px] h-5">
        <CircleCheck color="#09090B" height={10} width={10} />
        <span className="text-[11px] font-600 leading-4">Tamamlandı</span>
      </div>
    );
  }
  if (status === "waiting") {
    return (
      <div className="bg-[#F4F4F5] text-foreground rounded-full px-[6px] py-1 flex items-center gap-[4px] h-5">
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4.16669 0.832031H5.83335M5.00002 5.83203L6.25002 4.58203M8.33335 5.83203C8.33335 7.67298 6.84097 9.16536 5.00002 9.16536C3.15907 9.16536 1.66669 7.67298 1.66669 5.83203C1.66669 3.99108 3.15907 2.4987 5.00002 2.4987C6.84097 2.4987 8.33335 3.99108 8.33335 5.83203Z"
            stroke="#09090B"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span className="text-[11px] font-600 leading-4">Bekleniyor</span>
      </div>
    );
  }
  return (
    <div className="bg-zinc-900 text-white rounded-full px-[6px] py-1 flex items-center gap-[4px] h-5">
      <Hourglass color="#F4F4F5" className="size-[10px]" />
      <span className="text-[11px] font-600 leading-4">Devam ediyor</span>
    </div>
  );
};
